import { useQuery } from '@tanstack/vue-query'
import { computed, ref, unref, watch, type MaybeRef } from 'vue'

import { OlympusClient } from '@/OlympusClient'

const QUERY_KEY_PREFIX = ['billing', 'active-brand-plans'] as const

type ActiveBrandPlan = Awaited<
  ReturnType<
    typeof OlympusClient.billing.v2.brands.brand.subscriptions.getActivePlans
  >
>['plans'][number]

/** Boost plan identifier, typed so a rename in the Olympus plan enum breaks the build. */
const BOOST_PLAN: ActiveBrandPlan = 'boost'

/**
 * The brand's distinct non-cancelled subscription plans, mirrored into a module ref
 * so the static nav map can read it reactively. `undefined` means "not loaded yet" —
 * callers must not treat that as "no plans".
 */
const activeBrandPlans = ref<ActiveBrandPlan[] | undefined>(undefined)

/**
 *
 * @param brandId
 */
export function useActiveBrandPlansQuery(
  brandId: MaybeRef<string | undefined>
) {
  const query = useQuery({
    enabled: computed(() => !!unref(brandId)),
    queryFn: async ({ queryKey }) => {
      const [, , qBrandId] = queryKey

      if (!qBrandId) {
        throw new Error('Brand ID is required')
      }

      return await OlympusClient.billing.v2.brands.brand.subscriptions.getActivePlans(
        { params: { brandId: qBrandId } }
      )
    },
    queryKey: [...QUERY_KEY_PREFIX, brandId] as const,
    staleTime: 5 * 60 * 1000,
  })

  watch(
    query.data,
    (data) => {
      activeBrandPlans.value = data?.plans
    },
    { immediate: true }
  )

  return query
}

/**
 * Whether the brand is exclusively on the Boost plan. Returns `false` until plans
 * have loaded, so a not-yet-known plan never reads as Boost.
 */
export function isBrandBoostOnly(): boolean {
  const plans = activeBrandPlans.value
  return (
    plans !== undefined &&
    plans.length > 0 &&
    plans.every((plan) => plan === BOOST_PLAN)
  )
}

/** Whether the brand's active plans have been fetched yet. */
export function isBrandPlanLoaded(): boolean {
  return activeBrandPlans.value !== undefined
}

/**
 * The brand's first active plan, used to tag analytics. `undefined` until plans
 * load or when the brand has none.
 */
export function getPrimaryActiveBrandPlan(): ActiveBrandPlan | undefined {
  return activeBrandPlans.value?.[0]
}

export { activeBrandPlans }
